import {useState} from 'react';

import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {z} from 'zod';

import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {Alert, AlertDescription, AlertTitle} from '@common-web/components/ui/alert';
import {Separator} from '@common-web/components/ui/separator';
import {Checkbox} from '@common-web/components/ui/checkbox';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {PageHeader} from '@common-web/components/page-header';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';

import {Copy, ExternalLink, Plus, CheckCircle, AlertCircle, Loader2} from 'lucide-react';

import {usePortal} from '@common-web/providers/portalProvider';
import {useAuthStore} from '@common-web/stores/authStore';

import {createMutationErrorHandler, trpc} from '@common-web/lib/trpc';
import {reportError} from '@common-web/lib/errorReporting';

import {generateRandomPassword} from '@common/lib/utils';
import {useNavigate} from 'react-router-dom';

const createRfcSchema = z.object({
  clientId: z.string().optional(),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  companyName: z.string().min(1, 'Company name is required'),
  assignedBrokerId: z.string().optional(),
  section125FormEnabled: z.boolean(),
  mecFormEnabled: z.boolean(),
});

type CreateRfcForm = z.infer<typeof createRfcSchema>;

export function ClientCreateRfc() {
  const isInternalUser = useAuthStore((state) => state.isInternalUser());
  const isBrokerUser = useAuthStore((state) => state.isBrokerUser());
  const navigate = useNavigate();

  const portalContext = usePortal();
  const clientId = portalContext.portalType === 'client' ? portalContext.clientId : '';

  const [generatedRfc] = useState<{
    accessToken: string;
    password: string;
    rfcUrl: string;
  } | null>(null);
  const [copied, setCopied] = useState<'url' | 'password' | null>(null);

  const form = useForm<CreateRfcForm>({
    resolver: zodResolver(createRfcSchema),
    defaultValues: {
      clientId: undefined,
      password: '',
      companyName: '',
      assignedBrokerId: undefined,
      section125FormEnabled: false,
      mecFormEnabled: false,
    },
  });

  const clientsQuery = trpc.internal.client.list.useQuery(undefined, {
    enabled: isInternalUser,
    refetchOnWindowFocus: false,
  });

  const brokersQuery = trpc.clients.rfc.listBrokers.useQuery(undefined, {
    enabled: !isInternalUser && !isBrokerUser,
    refetchOnWindowFocus: false,
  });

  const createRfcMutation = trpc.clients.rfc.create.useMutation({
    onSuccess: (data) => {
      form.reset();
      // Broker users have their own deal route group (`/broker/deals/:rfcId`);
      // client and internal admins review deals at `/rfc/review/:rfcId`. Sending
      // everyone to the broker path bounced non-broker users to the homepage.
      const dealPath = isBrokerUser ? `/broker/deals/${data.id}` : `/rfc/review/${data.id}`;
      navigate(dealPath, {state: {password: data.password}});
    },
    onError: createMutationErrorHandler('RFC link creation failed'),
  });

  const generatePassword = () => {
    const password = generateRandomPassword();
    form.setValue('password', password);
  };

  const onSubmit = async (data: CreateRfcForm) => {
    try {
      await createRfcMutation.mutateAsync(data);
    } catch (error) {
      // createRfcMutation.onError already surfaces a toast.
      reportError(error, {silent: true, context: 'ClientCreateRfc.onSubmit'});
    }
  };

  const copyToClipboard = async (text: string, type: 'url' | 'password') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    } catch (error) {
      reportError(error, {
        title: 'Could not copy to clipboard',
        fallbackMessage: 'Copy the value manually instead.',
        context: 'ClientCreateRfc.copyToClipboard',
      });
    }
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        <PageHeader
          title='Create New Deal'
          description='Generate a secure deal link that groups can use to submit their forms'
          icon={<Plus className='h-6 w-6' />}
        />

        {/* Main Content */}
        <div className='px-8 py-6 max-w-7xl mx-auto w-full'>
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
            {/* Form Card */}
            <Card className='border-gray-200 shadow-sm'>
              <CardHeader className='bg-gray-50 border-b'>
                <CardTitle className='text-lg font-semibold text-brand-navy flex items-center gap-2'>
                  <Plus className='h-5 w-5 text-brand-blue' />
                  Generate Deal Link
                </CardTitle>
              </CardHeader>
              <CardContent className='p-6'>
                <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
                  {/* Client Selector - Internal only */}
                  {isInternalUser && (
                    <div>
                      <Label htmlFor='clientId' className='text-brand-navy'>
                        Client *
                      </Label>
                      {clientsQuery.isLoading ? (
                        <Skeleton className='h-10 w-full mt-1' />
                      ) : (
                        <Select
                          value={form.watch('clientId') || ''}
                          onValueChange={(value) => form.setValue('clientId', value)}
                        >
                          <SelectTrigger className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'>
                            <SelectValue placeholder='Select a client' />
                          </SelectTrigger>
                          <SelectContent>
                            {clientsQuery.data?.map((client) => (
                              <SelectItem key={client.id} value={client.id}>
                                {client.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {form.formState.errors.clientId && (
                        <p className='text-sm text-red-600 mt-1'>
                          {form.formState.errors.clientId.message}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Company Name */}
                  <div>
                    <Label htmlFor='companyName' className='text-brand-navy'>
                      Company Legal Name *
                    </Label>
                    <Input
                      id='companyName'
                      type='text'
                      placeholder='Enter company name'
                      {...form.register('companyName')}
                      className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
                    />
                    {form.formState.errors.companyName && (
                      <p className='text-sm text-red-600 mt-1'>
                        {form.formState.errors.companyName.message}
                      </p>
                    )}
                    <p className='text-xs text-brand-gray mt-1'>
                      This will be displayed to the client when they access the RFC form.
                    </p>
                  </div>

                  {/* Broker Assignment - Client users only (not internal, not broker) */}
                  {!isInternalUser &&
                    !isBrokerUser &&
                    brokersQuery.data &&
                    brokersQuery.data.length > 0 && (
                      <div>
                        <Label htmlFor='assignedBrokerId' className='text-brand-navy'>
                          Assign Broker (Optional)
                        </Label>
                        <Select
                          value={form.watch('assignedBrokerId') || 'none'}
                          onValueChange={(value) =>
                            form.setValue('assignedBrokerId', value === 'none' ? undefined : value)
                          }
                        >
                          <SelectTrigger className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'>
                            <SelectValue placeholder='No broker assigned' />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='none'>No broker assigned</SelectItem>
                            {brokersQuery.data.map((broker) => (
                              <SelectItem key={broker.id} value={broker.id}>
                                {broker.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className='text-xs text-brand-gray mt-1'>
                          If assigned, the broker will review the deal before it reaches you.
                        </p>
                      </div>
                    )}

                  {/* Forms Checkboxes */}
                  <div>
                    <Label htmlFor='forms' className='text-brand-navy'>
                      Forms
                    </Label>
                    <div className='flex flex-col space-y-2.5 mt-1'>
                      <div className='flex items-center space-x-2'>
                        <Checkbox
                          id='section125FormEnabled'
                          checked={form.watch('section125FormEnabled')}
                          onCheckedChange={(checked) =>
                            form.setValue('section125FormEnabled', !!checked, {
                              shouldValidate: true,
                            })
                          }
                        />
                        <Label htmlFor='section125FormEnabled' className='mb-0'>
                          125 Program Form
                        </Label>
                      </div>
                      {/* MEC Form option temporarily hidden
                      <div className='flex items-center space-x-2'>
                        <Checkbox
                          id='mecFormEnabled'
                          checked={form.watch('mecFormEnabled')}
                          onCheckedChange={(checked) =>
                            form.setValue('mecFormEnabled', !!checked, {
                              shouldValidate: true,
                            })
                          }
                        />
                        <Label htmlFor='mecFormEnabled' className='mb-0'>
                          MEC Form
                        </Label>
                      </div>
                      */}
                    </div>
                    <p className='text-xs text-brand-gray mt-2'>
                      Select which forms the group will need to complete.
                    </p>
                  </div>

                  {/* Password */}
                  <div>
                    <Label htmlFor='password' className='text-brand-navy'>
                      Access Password
                    </Label>
                    <div className='flex gap-2'>
                      <Input
                        id='password'
                        type='text'
                        placeholder='Enter or generate password'
                        {...form.register('password')}
                        className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
                      />
                      <Button
                        type='button'
                        variant='outline'
                        onClick={generatePassword}
                        className='shrink-0 transition-colors'
                      >
                        Generate
                      </Button>
                    </div>
                    {form.formState.errors.password && (
                      <p className='text-sm text-red-600 mt-1'>
                        {form.formState.errors.password.message}
                      </p>
                    )}
                    <p className='text-xs text-brand-gray mt-1'>
                      This password will be required to access and edit the RFC form.
                    </p>
                  </div>

                  {/* Submit */}
                  <Button
                    type='submit'
                    disabled={createRfcMutation.isPending}
                    className='w-full bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold shadow-sm transition-colors'
                  >
                    {createRfcMutation.isPending ? (
                      <>
                        <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                        Creating...
                      </>
                    ) : (
                      'Create Deal Link'
                    )}
                  </Button>

                  {/* Error */}
                  {!!createRfcMutation.error && (
                    <Alert variant='destructive'>
                      <AlertCircle className='h-4 w-4' />
                      <AlertTitle>Error</AlertTitle>
                      <AlertDescription>{createRfcMutation.error.message}</AlertDescription>
                    </Alert>
                  )}
                </form>
              </CardContent>
            </Card>

            {/* Right Panel: Generated Link or How it Works */}
            {generatedRfc ? (
              <Card className='border-gray-200 shadow-sm'>
                <CardHeader className='bg-gray-50 border-b'>
                  <CardTitle className='text-lg font-semibold text-emerald flex items-center gap-2'>
                    <CheckCircle className='h-5 w-5' />
                    Deal Link Generated
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-4 p-6'>
                  <Alert className='border-emerald/20 bg-emerald/5'>
                    <AlertDescription className='text-brand-navy'>
                      <strong>Success!</strong> Your deal link has been created. Share this
                      information with the group to allow them to complete their forms.
                    </AlertDescription>
                  </Alert>
                  <div>
                    <Label className='text-brand-navy'>RFC Link</Label>
                    <div className='flex gap-2 mt-1'>
                      <Input
                        value={generatedRfc.rfcUrl}
                        readOnly
                        className='font-mono text-sm border-gray-200'
                      />
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        onClick={() => copyToClipboard(generatedRfc.rfcUrl, 'url')}
                        className='transition-colors'
                      >
                        {copied === 'url' ? (
                          <CheckCircle className='h-4 w-4 text-emerald' />
                        ) : (
                          <Copy className='h-4 w-4' />
                        )}
                      </Button>
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        onClick={() => {
                          window.open(generatedRfc.rfcUrl, '_blank');
                        }}
                        className='transition-colors'
                      >
                        <ExternalLink className='h-4 w-4' />
                      </Button>
                    </div>
                  </div>
                  <Separator />
                  <div>
                    <Label className='text-brand-navy'>Access Password</Label>
                    <div className='flex gap-2 mt-1'>
                      <Input
                        value={generatedRfc.password}
                        readOnly
                        className='font-mono text-lg font-bold border-gray-200'
                      />
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        onClick={() => copyToClipboard(generatedRfc.password, 'password')}
                        className='transition-colors'
                      >
                        {copied === 'password' ? (
                          <CheckCircle className='h-4 w-4 text-emerald' />
                        ) : (
                          <Copy className='h-4 w-4' />
                        )}
                      </Button>
                    </div>
                    <p className='text-xs text-brand-gray mt-1'>
                      The group will need this password to access the RFC form.
                    </p>
                  </div>
                  <Alert className='border-brand-blue/20 bg-brand-blue/5'>
                    <AlertDescription className='text-brand-navy'>
                      <strong>Next Steps:</strong>
                      <ol className='list-decimal list-inside mt-2 space-y-1 text-sm text-brand-gray'>
                        <li>Send the RFC link to the group</li>
                        <li>Provide them with the access password</li>
                        <li>Group completes the RFC form online</li>
                        <li>Monitor progress in RFC Administration</li>
                      </ol>
                    </AlertDescription>
                  </Alert>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={() => setGeneratedRfc(null)}
                    className='w-full transition-colors'
                  >
                    Create Another Deal
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card className='border-gray-200 shadow-sm'>
                <CardHeader className='bg-gray-50 border-b'>
                  <CardTitle className='text-lg font-semibold text-brand-navy'>
                    How it Works
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-4 p-6'>
                  <div className='space-y-3 text-sm'>
                    {/* Internal: 4 steps */}
                    {isInternalUser && (
                      <>
                        <HowItWorksStep
                          step={1}
                          title='Select Client'
                          description='Choose which client this RFC is for'
                        />
                        <HowItWorksStep
                          step={2}
                          title='Set Password'
                          description='Create or generate a secure access password'
                        />
                        <HowItWorksStep
                          step={3}
                          title='Share with Group'
                          description='Send the generated link and password to the group. They will see their company name displayed when they access the form.'
                        />
                        <HowItWorksStep
                          step={4}
                          title='Track Progress'
                          description='Monitor submission status on the RFC Administration page.'
                        />
                      </>
                    )}

                    {/* Client: 3 steps */}
                    {!isInternalUser && !isBrokerUser && (
                      <>
                        <HowItWorksStep
                          step={1}
                          title='Set Password'
                          description='Create or generate a secure access password'
                        />
                        <HowItWorksStep
                          step={2}
                          title='Share with Group'
                          description='Send the generated link and password to the group. They will see their company name displayed when they access the form.'
                        />
                        <HowItWorksStep
                          step={3}
                          title='Track Progress'
                          description='Monitor submission status on the RFC Administration page.'
                        />
                      </>
                    )}

                    {/* Broker: 3 steps */}
                    {isBrokerUser && (
                      <>
                        <HowItWorksStep
                          step={1}
                          title='Enter Company Details'
                          description='Provide the company name and select forms'
                        />
                        <HowItWorksStep
                          step={2}
                          title='Share Link'
                          description='Send the generated link and password to the company'
                        />
                        <HowItWorksStep
                          step={3}
                          title='Review & Approve'
                          description="Once the company submits, you'll be notified to review and approve the deal"
                        />
                      </>
                    )}
                  </div>
                  <Separator />
                  <Alert className='border-amber/20 bg-amber/5'>
                    <AlertDescription className='text-brand-navy'>
                      <strong>Security Note:</strong>{' '}
                      <span className='text-brand-gray'>
                        Each RFC link is unique and password-protected. Links can only be used by
                        one group and expire after completion.
                      </span>
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function HowItWorksStep({
  step,
  title,
  description,
}: {
  step: number;
  title: string;
  description: string;
}) {
  return (
    <div className='flex items-start gap-3'>
      <div className='shrink-0 w-6 h-6 rounded-full bg-brand-blue/10 text-brand-blue flex items-center justify-center text-xs font-bold'>
        {step}
      </div>
      <div>
        <p className='font-medium text-brand-navy'>{title}</p>
        <p className='text-brand-gray'>{description}</p>
      </div>
    </div>
  );
}
